import { useState, useCallback } from 'react';
import { message } from 'antd';
import { p9207Api, F90K } from '../../../services/p9207Api';
import { useAuth } from '../../../contexts/AuthContext';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './useP9207Mode';

export function useP9207Record() {
  const { user } = useAuth();
  const [record, setRecord] = useState<F90K | null>(null);
  const [formData, setFormData] = useState<Partial<F90K>>({});
  const [loading, setLoading] = useState(false);
  const { setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<{ data: F90K } | null> => {
    setLoading(true);
    try {
      const data = await p9207Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      return { data };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof F90K, value: F90K[keyof F90K]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof F90K): F90K[keyof F90K] => {
    return (formData as Record<string, F90K[keyof F90K]>)[key] ?? '';
  }, [formData]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: F90K) => void,
  ) => {
    setLoading(true);
    clearFieldErrors();
    try {
      const req = { master: formData as F90K };
      if (mode === 'add') {
        const result = await p9207Api.create(req);
        message.success(`新增成功: ${result.f90k_01}`);
        onSuccess(result);
      } else {
        const result = await p9207Api.update(formData.f90k_01!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        const summary = errors.slice(0, 5).map(er => er.message).join('\n');
        const extra = errors.length > 5 ? `\n...共 ${errors.length} 項` : '';
        message.error({ content: summary + extra, duration: 6, style: { whiteSpace: 'pre-line' } });
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, setFieldErrors, clearFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await p9207Api.delete(record.f90k_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    const today = new Date().toISOString().substring(0, 10) + 'T00:00:00Z';
    setFormData({
      f90k_11: user?.userId || '',
      f90k_12: today,
      f90k_80: 'V',
      f90k_91: user?.userId || '',
    });
  }, [user, clearFieldErrors]);

  /** Apply result after save */
  const applyResult = useCallback((result: F90K) => {
    setRecord(result);
    setFormData({ ...result });
  }, []);

  return {
    record, formData, loading,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, hasErrors,
  };
}
